import type { Unit, Pixel } from '@/data';
import type { MetadataFormat } from '@/data/Metadata';
import type Metadata from '@/data/Metadata';
import type { Image3DPageData } from '@/data/sources';
import { ImageLikeSource, SourceType } from '@/data/sources';
import type { ImageDimensions } from '@/data/sources/Source';
import { pageToPageData } from '@/http-client/utils/pageToPageData';
import MetadataParser from '../MetadataParser';
import type { Image3DRowFormat, Image3DSyncFormat } from '../UnitParser';
import UnitParser from '../UnitParser';

export interface StructureDataToParse extends MetadataFormat {
  structure?: unknown;
  name?: string;
  id?: string;
  pixelUnit: Unit<Pixel>;
  width: Pixel;
  height: Pixel;
  pages?: Array<Image3DPageData>;
  metadata: Metadata;
  svgImage: string;
}

export class Structure extends ImageLikeSource {
  svgImage: string;

  constructor(parsed: StructureDataToParse) {
    super({
      pixelUnit: parsed.pixelUnit,
      type: SourceType.Structure,
      width: parsed.width,
      height: parsed.height,
      pages: parsed.pages,
      metadata: parsed.metadata,
      name: parsed.name,
      isLinear: false,
    });
    this.svgImage = parsed.svgImage;
  }

  scaled(scale: number): ImageLikeSource {
    throw new Error(`Method not implemented. scale = ${scale}`);
  }
}

export interface StructureFormat extends MetadataFormat {
  structure?: unknown;
  id?: string;
  name?: string;
  info?: string;
  license?: string;
  contributors?: Record<string, string>;
  year?: string;
}

export function structureFormatFromNewFormat(sourceFormat: Map<string, unknown>): StructureFormat {
  return {
    info: sourceFormat.get('text') as string,
    license: sourceFormat.get('license') as string,
    contributors: sourceFormat.get('contributors') as Record<string, string>,
    id: sourceFormat.get('id') as string,
    name: sourceFormat.get('name') as string,
    year: sourceFormat.get('year') as string,
  };
}

export default class StructureParser {
  static ROW_HEIGHT = 150 as Pixel;

  static fromFormat(
    format: StructureFormat,
    dim?: ImageDimensions,
  ): Structure {
    const name = format?.name;
    const id = format?.id;

    const quarterNotePerMeasure = 4;
    const nbMeasuresInStructureTestPiece = 35.5;
    const NB_ONSET = quarterNotePerMeasure * (nbMeasuresInStructureTestPiece - 1);
    const PIXEL_BY_ONSET = 100 as Pixel;
    const TOTAL_WIDTH_IN_PIXEL = NB_ONSET * PIXEL_BY_ONSET;

    const positions: Image3DSyncFormat = StructureParser
      .generatePositions(TOTAL_WIDTH_IN_PIXEL, NB_ONSET);
    const pixelUnit: Unit<Pixel> = UnitParser.createImage3DUnit(positions);

    const ONE_ONSET_IN_PIXEL = PIXEL_BY_ONSET;
    const width: Pixel = dim?.width || (TOTAL_WIDTH_IN_PIXEL + ONE_ONSET_IN_PIXEL) as Pixel;
    const height = dim?.height || this.ROW_HEIGHT * 3 as Pixel;

    const metadata = MetadataParser.fromFormat(format);

    const firstPage = positions.pages[0];
    const getXInTheRow = (row: Image3DRowFormat) => row['onset-x'].map((XOnset) => XOnset.x);
    const getLinesInfoToGenerateSVG = (row: Image3DRowFormat) => {
      const lastXOnsetIndex = row['onset-x'].length - 1;
      const endX = row['onset-x'][lastXOnsetIndex].x;
      return {
        cellX: getXInTheRow(row),
        y: row.y,
        endX,
      };
    };

    // TODO extract the map map join in a method
    const svgImage = firstPage.rows
      .map(getLinesInfoToGenerateSVG)
      .map((value) => this.generateLine(value.cellX, value.y, value.endX))
      .join();

    const pages = positions.pages.map((page) => pageToPageData(page, { height, width }));

    return new Structure({
      name, id, pixelUnit, width, height, pages, metadata, svgImage,
    });
  }

  private static generatePositions(
    TOTAL_WIDTH_IN_PIXEL: number,
    NB_ONSET: number,
  ): Image3DSyncFormat {
    const lastXInFirstRow = 1400;
    const Y_MARGIN = 100;
    const START_X = 110;
    const STAFF_HEIGHT = 50;
    return {
      pages: [
        {
          image: '',
          rows: [
            {
              'start-x': START_X,
              'end-x': lastXInFirstRow,
              y: Y_MARGIN,
              height: this.ROW_HEIGHT,
              'onset-x': [
                { onset: 0, x: START_X },
                { onset: 8, x: 800 },
                { onset: 16, x: lastXInFirstRow },
              ],
              staves: [
                { top: Y_MARGIN, bottom: Y_MARGIN + STAFF_HEIGHT },
              ],
            },
            {
              'start-x': START_X,
              'end-x': TOTAL_WIDTH_IN_PIXEL - lastXInFirstRow,
              y: this.ROW_HEIGHT + (2 * Y_MARGIN),
              height: this.ROW_HEIGHT,
              'onset-x': [
                { onset: 16, x: START_X },
                { onset: 20, x: 2000 - lastXInFirstRow },
                { onset: 24, x: 2400 - lastXInFirstRow },
                { onset: 28, x: 2800 - lastXInFirstRow },
                { onset: 120, x: 12000 - lastXInFirstRow },
                { onset: NB_ONSET, x: TOTAL_WIDTH_IN_PIXEL - lastXInFirstRow },
              ],
              staves: [
                {
                  top: this.ROW_HEIGHT + (2 * Y_MARGIN),
                  bottom: this.ROW_HEIGHT + (2 * Y_MARGIN) + STAFF_HEIGHT,
                },
              ],
            },
          ],
        },
      ],
    };
  }

  private static generateLine(xList: number[], yLine:number, endX: number) {
    return xList.map((x, index, array) => {
      const nextX = array[index + 1] || endX;
      const width = nextX - x;
      if (width !== 0) {
        return StructureParser.generateCell(x, yLine, width, this.ROW_HEIGHT);
      }
      return '';
    }).join();
  }

  private static generateCell(x: number, y:number, width: number, height: number): string {
    const xShiftToAlignWithLabels = 7;
    return `<rect x="${x - xShiftToAlignWithLabels}" y="${y - 10}" width="${width}" height="${height + 20}" class="structure"></rect>`;
  }
}
